import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Users, BarChart3, LogOut, Radio, Lock, Plus, X, Clock, AlertCircle, ChevronRight, CalendarDays } from 'lucide-react';
import { useFacilitateAuth } from '../../context/FacilitateAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)tcl_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const STATUS_LABEL = {
  draft: 'Ready',
  live: 'Live',
  closed: 'Closed',
  pending_approval: 'Awaiting Approval',
  rejected: 'Rejected',
};

/* ── Inline Request Form (shown inline on empty state or as modal) ── */
const RequestForm = ({ onSuccess, onCancel, authHeaders, showCancel = true }) => {
  const [form, setForm] = useState({ event_name: '', event_date: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.event_name.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/sessions/request`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ event_name: form.event_name.trim(), event_date: form.event_date || null }),
      });
      if (res.ok) {
        onSuccess();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Request failed. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="request-session-form">
      <div>
        <label className="text-[#FAF8F3]/40 text-xs block mb-1.5">Event / Session Name *</label>
        <input
          value={form.event_name}
          onChange={e => setForm({ ...form, event_name: e.target.value })}
          placeholder="e.g. NHS Leadership Forum 2026"
          required
          autoFocus
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/15 focus:outline-none focus:border-[#C99316]/40"
          data-testid="request-name-input"
        />
      </div>
      <div>
        <label className="text-[#FAF8F3]/40 text-xs flex items-center gap-1.5 mb-1.5">
          <CalendarDays size={11} /> Event Date <span className="text-[#FAF8F3]/20">(optional)</span>
        </label>
        <input
          type="date"
          value={form.event_date}
          onChange={e => setForm({ ...form, event_date: e.target.value })}
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm focus:outline-none focus:border-[#C99316]/40"
          data-testid="request-date-input"
        />
      </div>
      {error && (
        <p className="text-red-400 text-xs flex items-center gap-1.5">
          <AlertCircle size={12} /> {error}
        </p>
      )}
      <div className="flex gap-3 pt-1">
        {showCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 border border-white/10 text-[#FAF8F3]/30 text-sm rounded-xl hover:border-white/20 hover:text-[#FAF8F3]/50 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-[#C99316] text-[#071B3A] py-3 rounded-xl text-sm font-bold hover:bg-[#C99316]/90 disabled:opacity-60 transition-colors"
          data-testid="request-submit-btn"
        >
          {submitting ? 'Sending request…' : 'Request Session'}
        </button>
      </div>
      <p className="text-[#FAF8F3]/20 text-[11px] text-center">
        You'll receive an email with your session link once approved.
      </p>
    </form>
  );
};

const FacilitateRooms = () => {
  const navigate = useNavigate();
  const { token, speaker, logout, loading } = useFacilitateAuth();
  const [rooms, setRooms] = useState([]);
  const [fetchingRooms, setFetchingRooms] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const authHeaders = useCallback(() => {
    const csrf = getCsrfToken();
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    if (csrf) h['X-CSRF-Token'] = csrf;
    return h;
  }, [token]);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sessions/facilitate/rooms`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        setRooms((await res.json()).rooms || []);
      } else if (res.status === 401) {
        navigate('/facilitate/login');
      }
    } catch {}
    setFetchingRooms(false);
  }, [authHeaders, navigate]);

  useEffect(() => {
    if (loading) return;
    if (!token) { navigate('/facilitate/login'); return; }
    fetchRooms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loading]);

  const handleLogout = async () => {
    await logout();
    navigate('/facilitate/login');
  };

  const onRequestSuccess = () => {
    setShowRequestModal(false);
    fetchRooms();
  };

  if (fetchingRooms) return (
    <div className="min-h-screen bg-[#071B3A] flex items-center justify-center text-[#FAF8F3]/30">Loading…</div>
  );

  const activeRooms = rooms.filter(r => !['pending_approval', 'rejected'].includes(r.status));
  const pendingRooms = rooms.filter(r => r.status === 'pending_approval');
  const rejectedRooms = rooms.filter(r => r.status === 'rejected');
  const hasAnyRooms = rooms.length > 0;

  return (
    <div className="min-h-screen bg-[#071B3A]" data-testid="facilitate-rooms">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>

      {/* ── Header ── */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-serif text-lg text-[#FAF8F3]">Facilitator Portal</h1>
            {speaker && <p className="text-[#FAF8F3]/30 text-xs mt-0.5">{speaker.name}</p>}
          </div>
          <div className="flex items-center gap-3">
            {hasAnyRooms && (
              <button
                onClick={() => setShowRequestModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#C99316] text-[#071B3A] text-sm font-semibold rounded-lg hover:bg-[#C99316]/90 transition-colors shadow-lg shadow-[#C99316]/20"
                data-testid="request-session-btn"
              >
                <Plus size={15} /> Request a Session
              </button>
            )}
            <button
              onClick={handleLogout}
              className="text-[#FAF8F3]/30 text-xs hover:text-[#FAF8F3]/60 flex items-center gap-1"
              data-testid="facilitate-logout-btn"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* ── Request Modal (for speakers who already have sessions) ── */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#071B3A] border border-[#C99316]/20 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-[#FAF8F3] text-base font-semibold font-serif">Request a Live Session</h2>
                <p className="text-[#FAF8F3]/30 text-xs mt-0.5">Submitted for admin approval</p>
              </div>
              <button onClick={() => setShowRequestModal(false)} className="text-[#FAF8F3]/20 hover:text-[#FAF8F3]/60">
                <X size={18} />
              </button>
            </div>
            <RequestForm authHeaders={authHeaders} onSuccess={onRequestSuccess} onCancel={() => setShowRequestModal(false)} />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 py-10">

        {/* ── Empty State: no rooms at all ── */}
        {!hasAnyRooms && (
          <div className="flex flex-col items-center">
            {/* Prominent CTA card */}
            <div className="w-full max-w-lg bg-white/[0.03] border border-[#C99316]/20 rounded-2xl overflow-hidden">
              {/* Gold accent top bar */}
              <div className="h-1 bg-gradient-to-r from-[#C99316] to-[#C99316]/30" />
              <div className="p-8">
                <div className="w-12 h-12 rounded-full bg-[#C99316]/15 flex items-center justify-center mb-5">
                  <Plus size={22} className="text-[#C99316]" />
                </div>
                <h2 className="font-serif text-2xl text-[#FAF8F3] mb-2">Request your first session</h2>
                <p className="text-[#FAF8F3]/40 text-sm leading-relaxed mb-8">
                  Tell us your event name and date. Once approved, you'll receive your unique session code and a participant link to share with your audience.
                </p>
                <RequestForm authHeaders={authHeaders} onSuccess={onRequestSuccess} showCancel={false} onCancel={() => {}} />
              </div>
            </div>

            {/* How it works */}
            <div className="mt-10 w-full max-w-lg grid grid-cols-3 gap-4">
              {[
                { step: '1', title: 'Request', desc: 'Submit your event name and date' },
                { step: '2', title: 'Approval', desc: 'Admin reviews and approves within 24 hrs' },
                { step: '3', title: 'Go Live', desc: 'Receive your link and share with the room' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="text-center">
                  <div className="w-8 h-8 rounded-full bg-[#C99316]/10 flex items-center justify-center text-[#C99316] text-sm font-mono mx-auto mb-2">{step}</div>
                  <p className="text-[#FAF8F3]/60 text-xs font-medium mb-1">{title}</p>
                  <p className="text-[#FAF8F3]/25 text-[11px] leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Pending requests ── */}
        {pendingRooms.length > 0 && (
          <div className="mb-8">
            <p className="text-[#FAF8F3]/30 text-[11px] font-medium uppercase tracking-widest mb-3">Awaiting Approval</p>
            <div className="space-y-2">
              {pendingRooms.map(room => (
                <div
                  key={room.code}
                  className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-5 py-4 flex items-center gap-4"
                  data-testid={"room-pending-" + room.code}
                >
                  <Clock size={15} className="text-amber-400 shrink-0 animate-pulse" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#FAF8F3] text-sm font-medium truncate">{room.name}</p>
                    {room.start_date && (
                      <p className="text-[#FAF8F3]/30 text-xs mt-0.5 flex items-center gap-1">
                        <CalendarDays size={10} /> {room.start_date}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] font-medium px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400 shrink-0">
                    Awaiting Approval
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Active sessions ── */}
        {activeRooms.length > 0 && (
          <div className="mb-8">
            <p className="text-[#FAF8F3]/30 text-[11px] font-medium uppercase tracking-widest mb-3">Your Sessions</p>
            <div className="space-y-3">
              {activeRooms.map(room => (
                <Link
                  key={room.code}
                  to={"/facilitate/room/" + room.code}
                  className="block bg-white/[0.02] border border-white/5 rounded-xl p-5 hover:border-[#C99316]/20 hover:bg-white/[0.04] transition-all group"
                  data-testid={"room-" + room.code}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      {room.status === 'live'
                        ? <Radio size={15} className="text-emerald-400 animate-pulse shrink-0" />
                        : <Lock size={15} className={room.status === 'closed' ? 'text-red-400/60 shrink-0' : 'text-slate-400 shrink-0'} />
                      }
                      <div className="min-w-0">
                        <p className="text-[#FAF8F3] font-medium text-sm truncate">{room.name}</p>
                        <p className="text-[#FAF8F3]/20 text-xs font-mono mt-0.5">{room.code}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={"text-[10px] font-medium px-2 py-0.5 rounded-full " + (room.status === 'live' ? 'bg-emerald-500/20 text-emerald-400' : room.status === 'closed' ? 'bg-red-500/15 text-red-400' : 'bg-white/5 text-white/35')}>
                        {STATUS_LABEL[room.status] || room.status}
                      </span>
                      <ChevronRight size={14} className="text-[#FAF8F3]/15 group-hover:text-[#C99316]/60 transition-colors" />
                    </div>
                  </div>
                  <div className="flex gap-5 text-[#FAF8F3]/20 text-xs mt-3 pl-6">
                    <span className="flex items-center gap-1"><BarChart3 size={11} /> {room.pulse_count || 0} completions</span>
                    <span className="flex items-center gap-1"><Users size={11} /> {room.share_count || 0} shared</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── "Request another session" card (when speaker already has sessions) ── */}
        {hasAnyRooms && (
          <button
            onClick={() => setShowRequestModal(true)}
            className="w-full border border-dashed border-[#C99316]/20 rounded-xl p-5 text-center hover:border-[#C99316]/40 hover:bg-[#C99316]/5 transition-all group"
            data-testid="request-another-btn"
          >
            <div className="flex items-center justify-center gap-2 text-[#C99316]/60 group-hover:text-[#C99316] transition-colors">
              <Plus size={15} />
              <span className="text-sm font-medium">Request another session</span>
            </div>
          </button>
        )}

        {/* ── Rejected ── */}
        {rejectedRooms.length > 0 && (
          <div className="mt-8 pt-6 border-t border-white/[0.04]">
            <p className="text-[#FAF8F3]/15 text-[11px] uppercase tracking-widest mb-3">Rejected Requests</p>
            {rejectedRooms.map(room => (
              <div key={room.code} className="text-[#FAF8F3]/20 text-xs py-1.5 flex items-center gap-2">
                <AlertCircle size={11} className="text-red-400/30" />
                <span>{room.name}</span>
                {room.rejection_reason && (
                  <span className="text-[#FAF8F3]/15 italic">— {room.rejection_reason}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilitateRooms;
